import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./thankyouMessages.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";

// same limits as the PHP admin (addquestions.php)
const MAX_PAIRS = 5;
const MAX_MESSAGE_LEN = 140;

const emptyPair = () => ({ message: "", start: "", end: "" });

/** digits only, and never 0 (an empty box is shown instead) */
const cleanNumber = (v) => {
  const digits = String(v).replace(/[^0-9]/g, "");
  return digits === "" || parseInt(digits, 10) < 1 ? "" : digits;
};

export default function ThankYouMessages() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [pairs, setPairs] = useState([emptyPair()]);
  const [bad, setBad] = useState({}); // index -> true for pairs that failed validation
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const list = Array.isArray(data.thankyou_message) ? data.thankyou_message : [];
    setPairs(
      list.length
        ? list.slice(0, MAX_PAIRS).map((p) => ({
            message: String(p.message ?? ""),
            start: cleanNumber(p.start),
            end: cleanNumber(p.end),
          }))
        : [emptyPair()]
    );
  }, [data]);

  const update = (i, patch) => setPairs((list) => list.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const addPair = () => {
    if (pairs.length >= MAX_PAIRS) {
      Swal.fire({ title: "Limit Reached", text: `You reached the limit of ${MAX_PAIRS} pairs.`, icon: "warning" });
      return;
    }
    setPairs([...pairs, emptyPair()]);
  };

  const removePair = (i) => setPairs(pairs.filter((_, idx) => idx !== i));

  const clearAll = () => {
    setPairs([]);
    setBad({});
  };

  const warn = (text) => Swal.fire({ title: "Validation Error!", text, icon: "warning" });

  const handleSave = async () => {
    if (pairs.length < 1) {
      warn("Please add at least 1 Message before saving.");
      return;
    }

    const flagged = {};
    const ranges = [];
    let hasEmpty = false;
    let hasRange = false;

    pairs.forEach((p, i) => {
      if (!p.message.trim() || p.start === "" || p.end === "") {
        hasEmpty = true;
        flagged[i] = true;
        return;
      }
      const start = parseInt(p.start, 10);
      const end = parseInt(p.end, 10);
      if (start >= end) {
        hasRange = true;
        flagged[i] = true;
      }
      ranges.push({ i, start, end });
    });

    if (hasEmpty) {
      setBad(flagged);
      warn("Fields cannot be empty.");
      return;
    }

    let hasGap = false;
    ranges.sort((a, b) => a.start - b.start);
    for (let k = 0; k < ranges.length - 1; k++) {
      if (ranges[k].end + 1 !== ranges[k + 1].start) {
        hasGap = true;
        flagged[ranges[k].i] = true;
        flagged[ranges[k + 1].i] = true;
      }
    }

    if (hasRange || hasGap) {
      setBad(flagged);
      let text = "Please correct the following issues:";
      if (hasRange) text += "\n- Start range must be less than end range.";
      if (hasGap) {
        text +=
          "\n- Please enter valid sequential ranges only (e.g., 1–5, 6–10, 11–15). Random numbers are not permitted.";
      }
      warn(text);
      return;
    }

    setBad({});
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            thankyou_message: pairs.map((p) => ({
              message: p.message.trim(),
              start: String(parseInt(p.start, 10)),
              end: String(parseInt(p.end, 10)),
            })),
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await Swal.fire({ title: "Success!", text: "Data has been saved.", icon: "success", timer: 1500, showConfirmButton: false });
      navigate("/admin/rules");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!currentTheme) {
    return (
      <div className="ty-empty">
        <p>No theme selected.</p>
        <button type="button" className="ty-btn" onClick={() => navigate("/admin")}>
          Go to themes
        </button>
      </div>
    );
  }

  if (status === "loading" && !data) return <div className="ty-empty">Loading...</div>;

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="ty-page">
        <div className="ty-card">
          <h4 className="ty-title">Thank you message customization</h4>
          <p className="ty-note">
            The message whose range contains the player's score is shown on the thank you page. Ranges must follow one
            another without gaps (e.g. 1–30, 31–60, 61–100).
          </p>

          {pairs.map((p, i) => (
            <div className="ty-pair" key={i}>
              <div className="ty-field ty-message">
                <label htmlFor={`ty-message-${i}`}>Message</label>
                <input
                  id={`ty-message-${i}`}
                  type="text"
                  maxLength={MAX_MESSAGE_LEN}
                  className={bad[i] ? "ty-bad" : ""}
                  value={p.message}
                  onChange={(e) => update(i, { message: e.target.value })}
                />
                <span className={`ty-counter${p.message.length >= 130 ? " ty-counter-high" : ""}`}>
                  {p.message.length} / {MAX_MESSAGE_LEN}
                </span>
              </div>
              <div className="ty-field">
                <label htmlFor={`ty-start-${i}`}>Start Range</label>
                <input
                  id={`ty-start-${i}`}
                  type="number"
                  min="1"
                  max="10000"
                  className={bad[i] ? "ty-bad" : ""}
                  value={p.start}
                  onKeyDown={(e) => ["e", "E", "+", "-", ".", ","].includes(e.key) && e.preventDefault()}
                  onChange={(e) => update(i, { start: cleanNumber(e.target.value) })}
                />
              </div>
              <div className="ty-field">
                <label htmlFor={`ty-end-${i}`}>End Range</label>
                <input
                  id={`ty-end-${i}`}
                  type="number"
                  min="1"
                  max="10000"
                  className={bad[i] ? "ty-bad" : ""}
                  value={p.end}
                  onKeyDown={(e) => ["e", "E", "+", "-", ".", ","].includes(e.key) && e.preventDefault()}
                  onChange={(e) => update(i, { end: cleanNumber(e.target.value) })}
                />
              </div>
              <button type="button" className="ty-btn ty-danger" title="Delete" onClick={() => removePair(i)}>
                <i className="fa-solid fa-trash" />
              </button>
            </div>
          ))}

          <div className="ty-actions">
            <button type="button" className="ty-btn" onClick={addPair}>
              <i className="fa-solid fa-plus" /> Add Pair
            </button>
            <button type="button" className="ty-btn ty-secondary" onClick={clearAll}>
              Clear All
            </button>
          </div>
        </div>

        <div className="ty-save">
          <button type="button" className="save-and-continue" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </>
  );
}
